import styled from '@emotion/styled';
import { useFlow } from '@stackflow/react';
import { openExternalLink } from '@utils/nativeUtils';
import { HTMLAttributes, MouseEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type HtmlLinkType = 'STAMP' | 'COUPON' | 'GIFT' | 'PREPAID_CARD' | 'EVENT' | 'NOTICE';

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, 'dangerouslySetInnerHTML'> {
    content: string;
}

/** 새소식 상세(이벤트·공지사항) 본문 HTML */
function BoardHtmlContent({ content, onClick, ...rest }: Props) {
    const { i18n: { language } } = useTranslation();
    const { push } = useFlow();

    const handleLinkType = useCallback((linkType: HtmlLinkType, contentId?: string) => {
        switch (linkType) {
        case 'STAMP':
            push('StampsActivity', {});
            break;
        case 'COUPON':
            push('CouponActivity', { type: 'MY' });
            break;
        case 'GIFT':
            push('GiftsActivity', { type: 'MAIN' });
            break;
        case 'PREPAID_CARD':
            push('MyPrepaidCardActivity', { tab: 'MY' });
            break;
        case 'EVENT':
            if (contentId) push('EventActivity', { eventId: contentId });
            break;
        case 'NOTICE':
            if (contentId) push('NoticeActivity', { noticeId: contentId });
            break;
        default:
        }
    }, [push]);

    const handleClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
        onClick?.(e);
        const target = (e.target as HTMLElement).closest<HTMLElement>('[data-link-type], a[href]');
        if (!target || !e.currentTarget.contains(target)) return;

        const { linkType, contentId } = target.dataset;
        if (linkType) {
            e.preventDefault();
            handleLinkType(linkType as HtmlLinkType, contentId);
            return;
        }
        const href = target.getAttribute('href');
        if (href) {
            e.preventDefault();
            openExternalLink(href, () => {
                window.open(href, '_blank');
            });
        }
    }, [onClick, handleLinkType]);

    return (<Content {...rest}
        lang={language}
        onClick={handleClick}
        dangerouslySetInnerHTML={{ __html: content }} />);
}

const Content = styled.div`
    color: var(--color-text-primary);
    font-size: 14px;
    line-height: 1.4;
    padding: 0 var(--spacing-4) 40px;
    width: 100%;
    overflow: hidden;
    font-weight: 400;

    img {
        max-width: 100%;
        -webkit-user-drag: none;
        -khtml-user-drag: none;
        -moz-user-drag: none;
        -o-user-drag: none;
        user-drag: none;
        -webkit-user-select: none;
        -khtml-user-select: none;
        -moz-user-select: none;
        -o-user-select: none;
        user-select: none;
    }

    ul {
        list-style-type: disc;
        list-style-position: outside;
        display: block;
        padding-left: 16px;
    }

    ol {
        list-style-type: decimal;
        list-style-position: outside;
        padding-inline-start: 16px;
    }

    li {
        display: list-item;
    }

    h1, h2, h3 {
        font-weight: 400;
    }

    h1 {
        font-size: 24px;
    }

    h2 {
        font-size: 20px;
    }

    h3 {
        font-size: 16px;
    }

    p {
        font-size: 14px;
        min-height: 20px;
    }
`;

export default BoardHtmlContent;
